import React, { useState } from "react";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { PostAdminEventsDTO } from "utils/helpers/models/admin-events/post-admin-events.dto";
import useUsers from "containers/user-management/useHooks";
import { warningToaster } from "utils/helpers/common/alert-service";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_IMAGES = 3;
const MIN_TIME_DIFFERENCE = 10; // minutes

interface FormData extends PostAdminEventsDTO {
  uploadImages?: File[];
  eventType?: string;
}

const CreateAmbassadorExperience = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      start: "",
      end: "",
      media_ids: [],
      uploadImages: [],
      eventType: "",
    },
  });

  const { uploadFile, addAmbassadorExperience } = useUsers();
  const navigate = useNavigate();
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const goToBack = () => {
    navigate(-1);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    // Check maximum images limit
    if (selectedFiles.length + fileArray.length > MAX_IMAGES) {
      warningToaster(`You can only upload a maximum of ${MAX_IMAGES} images`);
      return;
    }

    // Check file sizes
    const oversizedFiles = fileArray.filter(
      (file) => file.size > MAX_FILE_SIZE
    );
    if (oversizedFiles.length > 0) {
      warningToaster(`${oversizedFiles.length} image(s) exceed 2MB limit`);
      return;
    }

    // Create previews
    const newPreviews = fileArray.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...newPreviews]);
    setSelectedFiles((prev) => [...prev, ...fileArray]);
    setValue("uploadImages", [...selectedFiles, ...fileArray]);
  };

  const removeImage = (index: number) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setValue(
      "uploadImages",
      selectedFiles.filter((_, i) => i !== index)
    );
  };

  const onSubmit = async (data: FormData) => {
    // Validate required fields
    if (!data.name) {
      warningToaster("Event name is required");
      return;
    }
    if (!data.description) {
      warningToaster("Description is required");
      return;
    }
    if (!data.start) {
      warningToaster("Start date and time is required");
      return;
    }
    if (!data.end) {
      warningToaster("End date and time is required");
      return;
    }
    if (!data.eventType) {
      warningToaster("Event type is required");
      return;
    }
    if (selectedFiles.length === 0) {
      warningToaster("At least one image is required");
      return;
    }

    // Validate date range
    const startDate = new Date(data.start);
    const endDate = new Date(data.end);

    // Check if both dates are on the same day
    if (
      startDate.getFullYear() !== endDate.getFullYear() ||
      startDate.getMonth() !== endDate.getMonth() ||
      startDate.getDate() !== endDate.getDate()
    ) {
      warningToaster("Start and end times must be on the same date");
      return;
    }

    // Check minimum time difference (10 minutes)
    const timeDifferenceInMinutes =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60);
    if (timeDifferenceInMinutes < MIN_TIME_DIFFERENCE) {
      warningToaster(
        `End time must be at least ${MIN_TIME_DIFFERENCE} minutes after start time`
      );
      return;
    }

    // Upload all images and get their IDs
    const mediaIds: number[] = [];
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append("file", file);
      const uploadResponse: any = await uploadFile(formData);
      if (uploadResponse && uploadResponse.id) {
        mediaIds.push(uploadResponse.id);
      }
    }

    if (mediaIds.length === 0) {
      warningToaster("Failed to upload images");
      return;
    }

    // Prepare payload
    const payload: PostAdminEventsDTO = {
      name: data.name,
      description: data.description,
      start: new Date(data.start).toISOString(),
      end: new Date(data.end).toISOString(),
      eventType: data.eventType,
      media_ids: mediaIds,
    };

    await addAmbassadorExperience(payload);
  };

  return (
    <div>
      <h1 className="text-base text-primaryText font-bold flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Create Ambassador Experience
      </h1>

      <div className="my-4">
        <form onSubmit={handleSubmit(onSubmit)} className="">
          <div className="bg-white p-6 rounded-lg space-y-6">
            {/* Event Name & Type - single row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="custom-label">Event Name</label>
                <input
                  type="text"
                  {...register("name", {
                    required: "Event name is required",
                  })}
                  className="custom-input"
                />
                {errors.name && (
                  <span className="text-red-500 text-sm">
                    {errors.name.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <label className="custom-label">Event Type</label>
                <select
                  {...register("eventType", {
                    required: "Event type is required",
                  })}
                  className="custom-input bg-white"
                >
                  <option value="">Select Event Type</option>
                  <option value="TRAINING">Training</option>
                  <option value="WELCOME">Welcome</option>
                </select>
                {errors.eventType && (
                  <span className="text-red-500 text-sm">
                    {errors.eventType.message}
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1">
            <label className="block custom-label">
                Description
              </label>
              <textarea
                {...register("description", {
                  required: "Description is required",
                })}
                rows={4}
                className="w-full min-h-[51px] px-4 py-2 border border-lightGrayBorder rounded-lg outline-none "
              />
              {errors.description && (
                <span className="text-red-500 text-sm">
                  {errors.description.message}
                </span>
              )}
            </div>

            {/* Start and End Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
              <label className="block custom-label">
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  {...register("start", {
                    required: "Start date and time is required",
                  })}
                  className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
                />
                {errors.start && (
                  <span className="text-red-500 text-sm">
                    {errors.start.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <label className="block custom-label">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  {...register("end", {
                    required: "End date and time is required",
                  })}
                  className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
                />
                {errors.end && (
                  <span className="text-red-500 text-sm">
                    {errors.end.message}
                  </span>
                )}
              </div>
            </div>

            {/* Multiple Image Upload */}
            <div className="flex flex-col gap-2">
              <label className="block text-labelText font-semibold mb-1">
                Upload Images
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                id="imageUpload"
                onChange={handleImageUpload}
              />
              <label
                htmlFor="imageUpload"
                className="cursor-pointer text-forestText w-[145px] h-[50px] flex justify-center items-center bg-paperWhiteBg rounded-full hover:bg-gray-200 transition"
              >
                + Upload Images
              </label>
              <p className="text-sm text-gray-500">
                You can upload up to {MAX_IMAGES} images (max 2MB each)
              </p>

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="flex flex-wrap gap-4 mt-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative w-24 h-24">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover rounded border"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 text-sm hover:bg-red-600"
                        onClick={() => removeImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              type="submit"
              className="bg-darkSlateBlueBg text-white px-6 py-2 rounded hover:bg-opacity-90 transition"
            >
              Create Experience
            </button>
            <button
              type="button"
              onClick={goToBack}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAmbassadorExperience;
